<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="space-x-3">
                <ButtonSecondaryIconSm @click="$emit('stepBack')">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span>{{ $t("love.google_reviews.add") }}</span>
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <div class="space-y-1">
                    <p class="text-base font-medium ct-text-1">
                        {{ $t("love.google_reviews.add_title") }} "{{ place.name }}"
                    </p>
                    <p class="ct-text">
                        {{ $t("love.google_reviews.info") }}
                    </p>
                </div>
                <FormSelect
                    :value="ratingFilter"
                    v-model="ratingFilter"
                    :label="$t('love.google_reviews.filter')"
                    :options="ratingOptions"
                />
                <div class="grid gap-3 grid-cols-2">
                    <ButtonSecondarySm @click="selectAll">
                        {{ $t("love.google_reviews.select_all") }}
                    </ButtonSecondarySm>
                    <ButtonSecondarySm
                        @click="deselectAll"
                        :disabled="!selectedTestimonials.length"
                    >
                        {{ $t("love.google_reviews.deselect_all") }}
                    </ButtonSecondarySm>
                </div>

                <CardPreloader v-if="isLoading" />
                <div class="space-y-2.5" v-else>
                    <p class="text-sm font-medium ct-text-1">
                        {{ $t("love.google_reviews.found") }} {{ filteredTestimonials.length }}
                        {{ $t("love.google_reviews.results_for") }} “{{ place.name }}”
                        {{ $t("love.google_reviews.with") }} {{ $t("love.at_least") }}
                        {{ ratingFilter }}
                        <StarIcon class="w-4 h-4 inline-block align-text-bottom" />.
                    </p>
                    <FormVariantCheck
                        v-for="testimonial in filteredTestimonials"
                        :key="testimonial.id"
                        v-model="testimonial.isSelected"
                    >
                        <p class="ct-text-1 font-medium text-sm">{{ testimonial.name }}</p>
                        <p class="ct-text-2 text-sm">{{ testimonial.content }}</p>
                        <p class="mt-2 ct-text">{{ testimonial.info }}</p>
                    </FormVariantCheck>
                </div>
            </div>
        </template>
        <template #sidebarFooter>
            <ButtonPrimaryMd
                class="w-full"
                :disabled="!selectedTestimonials.length"
                @click="handleAddSelected()"
            >
                {{ $t("love.google_reviews.add_selected") }}
            </ButtonPrimaryMd>
        </template>
    </PageSidebar>
</template>

<script>
import { PageSidebar } from "@easy/ui/page-components";
import { FormSelect, FormVariantCheck } from "@easy/ui/forms";
import { CardPreloader } from "@easy/ui/cards";
import { ButtonPrimaryMd, ButtonSecondaryIconSm, ButtonSecondarySm } from "@easy/ui/buttons";
import { ArrowLeftIcon, StarIcon } from "@easy/ui/icons";
import { googleService } from "@/apps/love/services/google.service";

export default {
    components: {
        FormSelect,
        ButtonPrimaryMd,
        PageSidebar,
        FormVariantCheck,
        ButtonSecondaryIconSm,
        ButtonSecondarySm,
        ArrowLeftIcon,
        CardPreloader,
        StarIcon,
    },
    props: {
        place: {
            type: Object,
            default: () => ({
                id: "",
                name: "",
            }),
        },
    },
    emits: ["stepBack", "addSelected"],
    data() {
        return {
            ratingFilter: 5,
            testimonials: [],
            isLoading: false,
        };
    },
    computed: {
        selectedTestimonials() {
            return this.filteredTestimonials.filter((t) => t.isSelected);
        },
        filteredTestimonials() {
            return this.testimonials.filter((i) => i.rate >= this.ratingFilter);
        },
        ratingOptions() {
            return [
                {
                    value: 1,
                    label: `${this.$t("love.at_least")} 1 ${this.$t("love.stars", 1)}`,
                },
                {
                    value: 2,
                    label: `${this.$t("love.at_least")} 2 ${this.$t("love.stars", 2)}`,
                },
                {
                    value: 3,
                    label: `${this.$t("love.at_least")} 3 ${this.$t("love.stars", 3)}`,
                },
                {
                    value: 4,
                    label: `${this.$t("love.at_least")} 4 ${this.$t("love.stars", 4)}`,
                },
                {
                    value: 5,
                    label: `${this.$t("love.at_least")} 5 ${this.$t("love.stars", 5)}`,
                },
            ];
        },
    },
    methods: {
        async searchForPlaceReviews() {
            try {
                this.isLoading = true;
                this.testimonials = await googleService.getPlaceDetails({
                    placeId: this.place.id,
                });
            } catch {
                //empty
            } finally {
                this.isLoading = false;
            }
        },
        handleAddSelected() {
            this.$emit("addSelected", [...this.selectedTestimonials]);
            for (const t of this.testimonials) {
                t.isSelected = false;
            }
        },
        selectAll() {
            for (const testimonial of this.testimonials) {
                testimonial.isSelected = true;
            }
        },
        deselectAll() {
            for (const testimonial of this.testimonials) {
                testimonial.isSelected = false;
            }
        },
    },
    watch: {
        place: {
            handler(newPlace, oldPlace) {
                this.testimonials = [];
                if (newPlace.id !== oldPlace.id && newPlace.id) {
                    this.searchForPlaceReviews();
                }
            },
            deep: true,
        },
    },
};
</script>
